import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import RefreshIcon from 'mdi-react/RefreshIcon';
import LoadingIcon from 'mdi-react/LoadingIcon';
import AccountOffOutlineIcon from 'mdi-react/AccountOffOutlineIcon';
import AccountCheckOutlineIcon from 'mdi-react/AccountCheckOutlineIcon';
import PinOutlineIcon from 'mdi-react/PinOutlineIcon';
import PinOffOutlineIcon from 'mdi-react/PinOffOutlineIcon';
import { Badge } from 'reactstrap';
import BlockUserModal from './BlockUserModal';
import ChatAnnouncementForm from './ChatAnnouncementForm';
import useChatUserBlocking from './useChatUserBlocking';
import DeleteModal from '../../../shared/components/custom/DeleteModal';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';
import useModal from '../../../shared/hooks/useModal';
import { formatDateTime } from '../../../util/date-and-time';
import Api from '../../../util/api';
import { partial } from '../../../util/translation/translation';

const p = partial('chat');

function getChatMessages(response) {
  if (Array.isArray(response)) {
    return response;
  }

  return (
    response?.messages ||
    response?.messageTree ||
    response?.chat?.messages ||
    response?.chat?.messageTree ||
    []
  );
}

function senderOf(message) {
  const userId = message.userId || message.sender?.userId;
  const userName =
    message.employeeDetails?.userName ||
    message.employeeDetails?.employeeName ||
    message.userName ||
    message.name ||
    '-';
  if (message.sender?.type !== 'company_employee') {
    return { userId, userName };
  }
  return {
    userId,
    userName,
    companyId: message.employeeDetails?.companyId || message.sender?.companyId,
    companyName: message.employeeDetails?.companyName,
  };
}

const linkOrText = (to, text, className) =>
  to ? (
    <Link to={to} className={className}>
      {text}
    </Link>
  ) : (
    <span className={className}>{text}</span>
  );

// One chip per emoji with a count; the tooltip lists who reacted.
function groupReactions(reactions = []) {
  const groups = new Map();
  reactions.forEach((reaction) => {
    const group = groups.get(reaction.emoji) || {
      emoji: reaction.emoji,
      names: [],
    };
    group.names.push(reaction.userName || '-');
    groups.set(reaction.emoji, group);
  });
  return [...groups.values()];
}

// A poll's options with votes and share, as the app shows them
const PollResults = ({ poll }) => {
  const total = poll.total || 0;
  return (
    <div style={{ marginBottom: 8, maxWidth: 360 }}>
      {poll.options.map((option) => {
        const votes = poll.results?.[option.id] || 0;
        const share = total ? Math.round((100 * votes) / total) : 0;
        return (
          <div key={option.id} style={{ marginBottom: 4 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 13,
              }}
            >
              <span>{option.text}</span>
              <span className="text-muted">
                {votes} · {share} %
              </span>
            </div>
            <div style={{ height: 6, background: '#EEF0F3', borderRadius: 3 }}>
              <div
                style={{
                  width: `${share}%`,
                  height: 6,
                  background: '#0469DC',
                  borderRadius: 3,
                }}
              />
            </div>
          </div>
        );
      })}
      <small className="text-muted">{p('pollVotes', { count: total })}</small>
    </div>
  );
};

function countMessages(messages = []) {
  return messages.reduce(
    (total, message) => total + 1 + countMessages(message.replies),
    0
  );
}

const ChatMessagesList = ({ chatId }) => {
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [handleModal, visible, deleteID] = useModal();
  const { isBlocked, blockUser, unblockUser } = useChatUserBlocking();
  // The user waiting for the admin to confirm the block
  const [userToBlock, setUserToBlock] = useState(null);
  // Open reports per message id
  const [reportCounts, setReportCounts] = useState({});

  const fetchMessages = useCallback(async () => {
    if (!chatId) return;

    setLoadingMessages(true);
    try {
      const [chatWithMessages, reports] = await Promise.all([
        Api.chat.getAccessibleChat(chatId),
        Api.chat.getReports(chatId).catch(() => []),
      ]);
      setMessages(getChatMessages(chatWithMessages));
      setReportCounts(
        reports.reduce(
          (counts, report) => ({
            ...counts,
            [report.messageId]: (counts[report.messageId] || 0) + 1,
          }),
          {}
        )
      );
    } catch (err) {
      toast.error(err || p('errorLoadingMessages'));
    } finally {
      setLoadingMessages(false);
    }
  }, [chatId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const deleteMessage = async () => {
    try {
      await Api.chat.deleteMessage(deleteID);
      toast.success(p('messageDeleted'));
    } catch {
      toast.error(p('errorDeletingMessage'));
    } finally {
      handleModal();
      fetchMessages();
    }
  };

  const togglePin = async (message) => {
    try {
      await Api.chat.pinMessage(message.id, !message.pinnedAt);
      toast.success(
        message.pinnedAt ? p('messageUnpinned') : p('messagePinned')
      );
      fetchMessages();
    } catch (err) {
      toast.error(err || p('pinERROR'));
    }
  };

  const renderMessage = (message, depth) => {
    const sender = senderOf(message);
    const initial = (sender.companyName || sender.userName || '?')
      .trim()[0]
      ?.toUpperCase();
    return (
      <div
        className={`chat-message${depth ? ' chat-message--reply' : ''}`}
        key={`chat-message-${message.id}`}
      >
        <span
          className={`chat-message__avatar${
            sender.companyId ? ' chat-message__avatar--company' : ''
          }`}
          aria-hidden
        >
          {initial}
        </span>
        <div className="chat-message__body">
          <div className="chat-message__meta">
            {sender.companyId || sender.companyName ? (
              <>
                {linkOrText(
                  sender.companyId &&
                    `/companies/edit/${sender.companyId}?tab=1`,
                  sender.companyName || '-',
                  'chat-message__sender'
                )}
                {linkOrText(
                  sender.userId && `/users/edit/${sender.userId}`,
                  sender.userName,
                  'chat-message__employee'
                )}
              </>
            ) : (
              linkOrText(
                sender.userId && `/users/edit/${sender.userId}`,
                sender.userName,
                'chat-message__sender'
              )
            )}
            {message.createdAt && (
              <span className="chat-message__time">
                {formatDateTime(message.createdAt)}
              </span>
            )}
            {message.editedAt && (
              <span className="chat-message__time">{p('edited')}</span>
            )}
            {message.pinnedAt && (
              <Badge color="info" className="ml-2">
                {p('pinnedBadge')}
              </Badge>
            )}
            {reportCounts[message.id] > 0 && (
              <Badge color="danger" className="ml-2">
                {p('reportCount', { count: reportCounts[message.id] })}
              </Badge>
            )}
            {sender.userId && isBlocked(sender.userId) && (
              <Badge color="secondary" className="ml-2">
                {p('userIsBlocked')}
              </Badge>
            )}
          </div>
          <p className="chat-message__text">{message.text}</p>
          {message.imageUrl && (
            <a href={message.imageUrl} target="_blank" rel="noreferrer">
              <img
                src={message.imageUrl}
                alt=""
                style={{
                  maxWidth: 240,
                  maxHeight: 180,
                  borderRadius: 8,
                  marginBottom: 8,
                  display: 'block',
                }}
              />
            </a>
          )}
          {message.poll && <PollResults poll={message.poll} />}
          {message.reactions?.length > 0 && (
            <div className="chat-message__reactions">
              {groupReactions(message.reactions).map((group) => (
                <span
                  key={group.emoji}
                  className="chat-reaction"
                  title={group.names.join(', ')}
                >
                  {group.emoji}
                  {group.names.length > 1 && (
                    <span className="chat-reaction__count">
                      {group.names.length}
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}
          {message.replies?.length > 0 && (
            <div className="chat-message__replies">
              {message.replies.map((reply) => renderMessage(reply, depth + 1))}
            </div>
          )}
        </div>
        <div className="chat-message__actions">
          <SquareIconButton
            icon={message.pinnedAt ? <PinOffOutlineIcon /> : <PinOutlineIcon />}
            title={message.pinnedAt ? p('unpinMessage') : p('pinMessage')}
            id={`pin-message-${message.id}`}
            onClick={() => togglePin(message)}
          />
          {sender.userId &&
            (isBlocked(sender.userId) ? (
              <SquareIconButton
                icon={<AccountCheckOutlineIcon />}
                title={p('unblockUser')}
                id={`unblock-user-${message.id}`}
                onClick={() => unblockUser(sender.userId)}
              />
            ) : (
              <SquareIconButton
                icon={<AccountOffOutlineIcon />}
                title={p('blockUser')}
                tone="danger"
                id={`block-user-${message.id}`}
                onClick={() =>
                  setUserToBlock({
                    userId: sender.userId,
                    userName: sender.userName,
                  })
                }
              />
            ))}
          <SquareIconButton
            icon={<DeleteOutlineIcon />}
            title={p('deleteMessage')}
            tone="danger"
            id={`delete-message-${message.id}`}
            onClick={() => handleModal(message.id)}
          />
        </div>
      </div>
    );
  };

  const total = countMessages(messages);

  return (
    <div className="chat-messages">
      <DeleteModal
        visible={visible}
        type={p('message')}
        handleModal={handleModal}
        modalAction={deleteMessage}
      />
      <BlockUserModal
        user={userToBlock}
        onCancel={() => setUserToBlock(null)}
        onBlock={(block) => {
          blockUser(block);
          setUserToBlock(null);
        }}
      />
      <ChatAnnouncementForm chatId={chatId} onPosted={fetchMessages} />
      <div className="chat-messages__header">
        <span>{p('messageCount', { count: total })}</span>
        <SquareIconButton
          icon={<RefreshIcon className={loadingMessages ? 'spin' : ''} />}
          title={p('refresh')}
          id="refresh-chat-messages"
          disabled={loadingMessages}
          onClick={fetchMessages}
        />
      </div>
      {loadingMessages && !messages.length && (
        <div className="chat-messages__empty">
          <LoadingIcon className="stats-spinner" aria-hidden />
        </div>
      )}
      {!loadingMessages && !messages.length && (
        <div className="chat-messages__empty">{p('noMessages')}</div>
      )}
      {messages.map((message) => renderMessage(message, 0))}
    </div>
  );
};

export default ChatMessagesList;
